import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { IconAlertCircle, IconFileText, IconPlus } from '@tabler/icons-react';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useChangesList } from 'src/api/queries/changes/useChangeQueries';
import { useChangeLogPdf } from 'src/api/queries/changes/useChangeMutations';
import { IContact } from 'src/api/models/Organization';
import { resolveWorkflowButtonColor } from 'src/utils';
import { WorkflowCreateButton } from 'src/app/workflows/WorkflowCreateButton';
import { WorkflowLogModal } from 'src/app/workflows/WorkflowLogModal';
import { useJobs } from 'src/hooks/useJobs';
import { Header } from 'src/design/organisms/Header';
import { jobBreadcrumbs } from 'src/app/workflows/jobBreadcrumbs';
import { usePdfBlobUrl } from 'src/design/molecules/usePdfBlobUrl';
import { WorkflowStatusPanel } from 'src/design/molecules/WorkflowStatusPanel';
import { ChangesTable } from './components/ChangesTable';
import { useWorkflowStatusFilters } from 'src/app/workflows/useWorkflowStatusFilters';
import { useWorkflowStatusSegments } from 'src/app/workflows/useWorkflowStatusSegments';
import { useWorkflowLogPdf } from 'src/app/workflows/useWorkflowLogPdf';

export const Changes: React.FC = () => {
  const { selectedJob } = useJobs();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: changeList, isLoading: loading } = useChangesList(
    selectedJob?.id,
  );
  const changes = React.useMemo(() => changeList ?? [], [changeList]);
  const {
    filters,
    covered,
    selectableStatuses,
    statusFilter,
    setStatusFilter,
    filteredItems,
  } = useWorkflowStatusFilters(changes, selectedJob?.changeOrderWorkflow);
  const segments = useWorkflowStatusSegments(
    changes,
    filters,
    covered,
    selectedJob?.changeOrderWorkflow,
  );

  const [logOpen, setLogOpen] = React.useState(false);
  const [logStatuses, setLogStatuses] = React.useState<string[]>([]);
  const [logContact, setLogContact] = React.useState<IContact | null>(null);
  const logPdf = useChangeLogPdf();
  const { file: logFile, loading: logLoading } = useWorkflowLogPdf({
    open: logOpen,
    jobId: selectedJob?.id,
    statuses: logStatuses,
    contact: logContact,
    fetchPdf: (jobId, statuses, contactId) =>
      logPdf.mutateAsync({ jobId, statuses, contactId }),
    logLabel: 'Failed to load change order log PDF',
    onError: () => setLogOpen(false),
  });
  const logUrl = usePdfBlobUrl(logFile?.data);

  const handleOpenLog = () => {
    if (!selectedJob) return;
    setLogOpen(true);
  };

  const closeLog = () => setLogOpen(false);

  if (!selectedJob) return null;

  return (
    <>
      <Header
        breadcrumbProps={{
          items: jobBreadcrumbs(selectedJob, undefined, 'Changes'),
        }}
        title="Changes"
        helpText="Manage change orders for this job. Create new change requests, view existing ones, and track their status through the approval workflow."
      />

      <Group mb="md">
        {selectedJob.changeOrderWorkflow && (
          <WorkflowCreateButton
            workflow={selectedJob.changeOrderWorkflow}
            color={resolveWorkflowButtonColor(
              selectedJob.changeOrderWorkflow.createButtonColor,
            )}
            leftSection={<IconPlus />}
            onClick={() => navigate(`${location.pathname}/submit/change`)}
          >
            {selectedJob.changeOrderWorkflow.createButtonText ||
              'Create Change'}
          </WorkflowCreateButton>
        )}
        <SecondaryButton leftSection={<IconFileText />} onClick={handleOpenLog}>
          Change Order Log
        </SecondaryButton>
      </Group>

      {!selectedJob.changeOrderWorkflowId && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Missing Change Order Workflow"
          color="red"
          mb="md"
        >
          Default workflow does not exist. Please contact an administrator.
        </Alert>
      )}

      <WorkflowStatusPanel
        segments={segments}
        value={statusFilter}
        onChange={setStatusFilter}
      />

      <ChangesTable changes={filteredItems} loading={loading} />

      <WorkflowLogModal
        opened={logOpen}
        onClose={closeLog}
        title="Change Order Log"
        pdfTitle="Change Order Log PDF"
        statusOptions={selectableStatuses}
        statuses={logStatuses}
        onStatusChange={setLogStatuses}
        customerNumber={selectedJob.customerNumber?.trim() ?? ''}
        contact={logContact}
        onContactChange={setLogContact}
        logUrl={logUrl}
        logLoading={logLoading}
      />
    </>
  );
};
